import Alpine from "alpinejs";
import mask from "@alpinejs/mask";
import collapse from "@alpinejs/collapse";
import AOS from "aos";
import "aos/dist/aos.css";
import Swiper from "swiper";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import "./nbsp";
import {
  DEVIZY_MAP_STYLES,
  DEVIZY_MAP_DEFAULT_CENTER,
  DEVIZY_MAP_DEFAULT_ZOOM,
  DEVIZY_MAP_MARKER_URL,
  DEVIZY_MAP_MARKER_SIZE,
  DEVIZY_MAP_MARKER_ANCHOR,
} from "./map-styles";

/* Swiper */
Swiper.use([Navigation, Autoplay]);
window.Swiper = Swiper;

/*
 * Mapa poboček (úvodní stránka) — seznam měst nahoře, pod tím pobočky
 * vybraného města, mapa se přepíná podle vybrané pobočky. `branches` jde
 * z PHP (WP_Query nad CPT pobocka) jako JSON přímo do x-data.
 */
/*
 * google.maps.Map#setZoom() nemá žádnou animaci (na rozdíl od panTo,
 * který plynule posouvá) — při skoku o víc úrovní naráz to viditelně
 * "cukne". Přizoomujeme proto postupně po jedné úrovni, s krátkou
 * pauzou mezi kroky, ať to působí plynule.
 */
function devizySmoothZoomTo(map, targetZoom) {
  const currentZoom = map.getZoom();

  if (currentZoom === targetZoom) {
    return;
  }

  const step = targetZoom > currentZoom ? 1 : -1;
  google.maps.event.addListenerOnce(map, "zoom_changed", () => {
    devizySmoothZoomTo(map, targetZoom);
  });

  setTimeout(() => {
    map.setZoom(currentZoom + step);
  }, 90);
}

function devizyMapMarkerIcon() {
  return {
    url: DEVIZY_MAP_MARKER_URL,
    scaledSize: new google.maps.Size(
      DEVIZY_MAP_MARKER_SIZE.width,
      DEVIZY_MAP_MARKER_SIZE.height,
    ),
    anchor: new google.maps.Point(
      DEVIZY_MAP_MARKER_ANCHOR.x,
      DEVIZY_MAP_MARKER_ANCHOR.y,
    ),
  };
}

function pobockyMapa(branches, mapDefaults) {
  const firstBranch = branches[0] || null;

  // mapDefaults jde z PHP (admin "Ovládání webu" → Pobočky, viz
  // devizy_mapa_vychozi_nastaveni() v inc/ovladani-webu.php). Fallback na
  // konstanty z map-styles.js jen pro jistotu, kdyby komponenta byla
  // někdy použitá bez PHP wiringu.
  const defaultCenter = (mapDefaults && mapDefaults.center) || DEVIZY_MAP_DEFAULT_CENTER;
  const defaultZoom = (mapDefaults && mapDefaults.zoom) || DEVIZY_MAP_DEFAULT_ZOOM;

  return {
    branches,
    selectedCityId: firstBranch ? firstBranch.cityId : null,
    selectedBranchId: null,
    map: null,
    markers: {}, // branch.id -> google.maps.Marker, vytvořené jednou v initMap()
    infoWindow: null,

    get cities() {
      const seen = new Set();
      return this.branches
        .filter((b) => (seen.has(b.cityId) ? false : seen.add(b.cityId)))
        .map((b) => ({ id: b.cityId, name: b.cityName }));
    },

    get filteredBranches() {
      return this.branches.filter((b) => b.cityId === this.selectedCityId);
    },

    selectCity(cityId) {
      this.selectedCityId = cityId;
      this.selectedBranchId = null;

      if (this.infoWindow) {
        this.infoWindow.close();
      }

      if (!this.map) {
        return;
      }

      const cityBranches = this.filteredBranches.filter((b) => b.lat && b.lng);

      if (!cityBranches.length) {
        return;
      }

      // Jediná pobočka ve městě je jednoznačná volba — rovnou ji i
      // vybereme (tooltip, zvýraznění v seznamu), ne jen přizoomovat a
      // nutit uživatele ke druhému kliku. Při 2+ pobočkách zůstává jen
      // zoom na celé město, výběr konkrétní pobočky dál řeší uživatel.
      if (cityBranches.length === 1) {
        this.selectBranch(cityBranches[0].id);
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      cityBranches.forEach((b) => bounds.extend({ lat: b.lat, lng: b.lng }));
      this.map.fitBounds(bounds);
    },

    selectBranch(branchId) {
      const branch = this.branches.find((b) => b.id === branchId);

      if (!branch) {
        return;
      }

      this.selectedBranchId = branchId;
      this.selectedCityId = branch.cityId;

      const marker = this.markers[branchId];

      if (!this.map || !marker || !branch.lat || !branch.lng) {
        return;
      }

      const position = { lat: branch.lat, lng: branch.lng };
      this.map.panTo(position);
      devizySmoothZoomTo(this.map, 15);
      this.showInfoWindow(branch, marker);
    },

    // Body, které mají vyplněnou polohu — jediné, se kterými mapa
    // reálně pracuje (marker, fitBounds, klik).
    get branchesWithPosition() {
      return this.branches.filter((b) => b.lat && b.lng);
    },

    initMap() {
      if (typeof google === "undefined" || !google.maps) {
        return;
      }

      this.map = new google.maps.Map(this.$refs.mapa, {
        styles: DEVIZY_MAP_STYLES,
        center: defaultCenter,
        zoom: defaultZoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        rotateControl: false,
        zoomControl: true,
      });

      const withPosition = this.branchesWithPosition;

      // Výchozí pohled je defaultCenter/defaultZoom (admin "Ovládání webu"
      // → Pobočky), NE fitBounds přes všechny pobočky — s Mariánskými
      // Lázněmi na druhé straně republiky by fitBounds zbytečně oddálil
      // pohled na moravskoslezský klastr, kde je 12 z 13 poboček. Markery
      // se ale vytvářejí pro VŠECHNY pobočky s polohou — výběr
      // města/pobočky (selectCity/selectBranch) dál plně automaticky
      // přizoomuje/panuje, i na tu vzdálenou.
      withPosition.forEach((branch) => {
        const position = { lat: branch.lat, lng: branch.lng };

        const marker = new google.maps.Marker({
          position,
          map: this.map,
          icon: devizyMapMarkerIcon(),
        });
        marker.addListener("click", () => this.selectBranch(branch.id));

        this.markers[branch.id] = marker;
      });

      // Kontejner mapy je h-full — stretchuje se na výšku sloupce s
      // pobočkami vedle (ten se mění podle počtu poboček vybraného
      // města), ale Google Maps si velikost svého vykreslení sleduje
      // jen samo od sebe přes CSS, ne přes vlastní observer. Bez tohohle
      // by mapa po zvětšení/zmenšení kontejneru zůstala vykreslená v
      // PŮVODNÍ velikosti (prázdný pruh dole, nebo oříznutá). setCenter
      // po trigger("resize") je nutný, protože samotný resize event mapu
      // nepřecentruje — bez něj by se viditelný výřez při každé změně
      // výšky posunul.
      const mapResizeObserver = new ResizeObserver(() => {
        const center = this.map.getCenter();
        google.maps.event.trigger(this.map, "resize");
        if (center) {
          this.map.setCenter(center);
        }
      });
      mapResizeObserver.observe(this.$refs.mapa);
    },

    showInfoWindow(branch, marker) {
      if (!branch || !branch.name) {
        return;
      }

      if (!this.infoWindow) {
        this.infoWindow = new google.maps.InfoWindow({
          disableAutoPan: true,
          maxWidth: 260,
        });
      }

      const wrapper = document.createElement("div");
      wrapper.className =
        "flex flex-row items-center bg-primary text-white py-2 px-4 rounded-full gap-x-1.5";

      const label = document.createElement("div");
      label.className = "text-body-sm";
      label.textContent = branch.name;

      const closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.className = "ml-auto cursor-pointer shrink-0";
      closeBtn.setAttribute("aria-label", "Zavřít");
      closeBtn.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L17 17M17 1L1 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>';
      closeBtn.addEventListener("click", () => this.infoWindow.close());

      wrapper.appendChild(label);
      wrapper.appendChild(closeBtn);

      this.infoWindow.setContent(wrapper);
      this.infoWindow.open({ map: this.map, anchor: marker });
    },
  };
}

/**
 * Sticky "Obsah článku" menu na detailu novinky (single.php) — kotvy
 * generuje devizy_novinka_obsah_s_kotvami() (functions.php) z <h2>
 * nadpisů v obsahu. IntersectionObserver hlídá pásmo horních ~30 %
 * viewportu (rootMargin s velkým záporným bottom) — nadpis, který
 * do něj vjede, se stane aktivní kotvou. Aktualizujeme JEN při vjezdu
 * (isIntersecting === true), ne při výjezdu — díky tomu zůstává
 * poslední navštívená sekce aktivní, dokud nevjede další, přesně jak
 * se u scrollspy menu čeká.
 */
function devizyObsahClanku() {
  return {
    aktivniKotva: null,
    init() {
      const nadpisy = document.querySelectorAll("#clanek-obsah h2[id]");
      if (!nadpisy.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.aktivniKotva = entry.target.id;
            }
          });
        },
        { rootMargin: "0px 0px -70% 0px", threshold: 0 },
      );

      nadpisy.forEach((nadpis) => observer.observe(nadpis));
    },
  };
}

/* Alpine */
Alpine.plugin(mask);
Alpine.plugin(collapse);
Alpine.data("pobockyMapa", pobockyMapa);
Alpine.data("devizyObsahClanku", devizyObsahClanku);

// Globální store pro vysouvací modaly (viz inc/modaly.php) — díky store
// místo lokálního x-data může tlačítko, které modal otevírá, sedět úplně
// kdekoliv na stránce (jiná sekce, jiný Alpine komponent), ne jen jako
// sourozenec toho konkrétního modalu. "otevreny" nese klíč aktuálně
// otevřeného modalu (string) nebo null, když je vše zavřené — najednou
// tak může být otevřený vždy jen jeden.
// "kontaktPlacement" nese GA4 placement tlačítka, které kontaktní modal
// naposledy otevřelo (viz devizy_modal_tlacitko() v inc/modaly.php) —
// dataLayer push po úspěšném odeslání formuláře (níže) si ho odsud
// přečte, protože v tu chvíli už samotné tlačítko není k dispozici.
Alpine.store("modaly", { otevreny: null, kontaktPlacement: null });

window.Alpine = Alpine;
Alpine.start();

/* AOS */
AOS.init({
  once: true,
  offset: 120,
  duration: 400,
  easing: "ease",
  disable: () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
});

/*
 * GA4 měření konverzí — Consent Mode v2. Výchozí "denied" stav pošle
 * header.php ještě před načtením GTM; tahle funkce posílá skutečný
 * stav při každém souhlasu i jeho pozdější změně (přes "Upravit
 * nastavení"), ať Google tagy uvnitř GTM vědí, co smí odeslat.
 */
function devizySynchronizovatConsentMode() {
  if (typeof window.gtag !== "function") {
    return;
  }

  const marketingPovoleno = CookieConsent.acceptedCategory("marketing");

  window.gtag("consent", "update", {
    analytics_storage: CookieConsent.acceptedCategory("analytics") ? "granted" : "denied",
    ad_storage: marketingPovoleno ? "granted" : "denied",
    ad_user_data: marketingPovoleno ? "granted" : "denied",
    ad_personalization: marketingPovoleno ? "granted" : "denied",
  });
}

/* Cookies consent */
CookieConsent.run({
  onConsent: devizySynchronizovatConsentMode,
  onChange: devizySynchronizovatConsentMode,
  guiOptions: {
    consentModal: {
      layout: "box wide",
      position: "middle center",
      equalWeightButtons: false,
    },
    preferencesModal: {
      layout: "box inline",
      position: "right",
      equalWeightButtons: false,
    },
  },
  categories: {
    necessary: { readOnly: true },
    analytics: {},
    marketing: {},
    functional: {},
  },
  language: {
    default: "cs",
    autoDetect: "document",
    translations: {
      cs: {
        consentModal: {
          title: "Web používá cookies",
          description:
            "K personalizaci obsahu a reklam, poskytování funkcí sociálních médií a analýze naší návštěvnosti využíváme soubory cookie.",
          acceptAllBtn: "Povolit vše",
          acceptNecessaryBtn: "Zakázat vše",
          showPreferencesBtn: "Upravit nastavení",
        },
        preferencesModal: {
          title: "Předvolby souborů cookie",
          acceptAllBtn: "Povolit vše",
          acceptNecessaryBtn: "Zakázat vše",
          savePreferencesBtn: "Uložit nastavení",
          closeIconLabel: "Zavřít okno",
          serviceCounterLabel: "Služby",
          sections: [
            {
              title: "Použití souborů cookie",
              description:
                "Soubory cookie používáme k zajištění základních funkcí webu a ke zlepšení vašeho online zážitku.",
            },
            {
              title:
                'Nezbytné cookies <span class="pm__badge">Vždy povoleno</span>',
              description:
                "Tyto soubory cookie jsou nezbytné pro správné fungování webu.",
              linkedCategory: "necessary",
            },
            {
              title: "Analytické a statistické cookies",
              description:
                "Tyto cookies nám ukazují, jak web používáte, a pomáhají nám ho vylepšovat.",
              linkedCategory: "analytics",
            },
            {
              title: "Marketingové cookies",
              description:
                "Tyto soubory se používají pro účely reklam a měření jejich účinnosti.",
              linkedCategory: "marketing",
            },
            {
              title: "Funkční cookies",
              description:
                "Funkční cookies umožňují webu zapamatovat si vaše volby a předvolby.",
              linkedCategory: "functional",
            },
          ],
        },
      },
    },
  },
});

/*
 * Po úspěšném odeslání CF7 formuláře (výhody) necháme submit button
 * natrvalo disabled a hlášku vložíme přímo do něj, místo defaultního
 * boxu pod formulářem (CF7 by button jinak po dokončení AJAXu zase
 * odblokoval) — ten box necháváme v DOM jen pro screen readery.
 */
document.addEventListener("wpcf7mailsent", (event) => {
  const form = event.target;
  const button = form.querySelector(".wpcf7-submit");
  const message = event.detail?.apiResponse?.message;

  if (!button || !message) {
    return;
  }

  button.textContent = message;
  button.disabled = true;

  const responseOutput = form.querySelector(".wpcf7-response-output");
  if (responseOutput) {
    responseOutput.classList.add("sr-only");
  }
});

/*
 * Newsletter a registrační formulář (.form-newsletter, .form-registrace)
 * — text tlačítka zůstává beze změny, jen se mu na dobu odeslání vloží
 * malá rotující ikonka PŘED text (stejný SVG vzor jako v dokumentaci
 * Tailwindu — animate-spin, dvoubarevný kruh). Tlačítko je inline-flex
 * s gap-2 (.wpcf7-submit v theme.css), takže o mezeru mezi ikonkou a
 * textem se postará flexbox sám (mezera zmenšená přes
 * .form-newsletter/.form-registrace .wpcf7-submit v theme.css), žádné
 * ruční pozicování. CF7 vlastní spinner je u obou v CSS schovaný
 * (.wpcf7-spinner).
 *
 * Ikonka NEMÁ vlastní barvu (žádné text-white) — dědí currentColor po
 * tlačítku, ať je vidět i v hover stavu (tlačítko na hoveru mění text
 * z bílé na primary, viz .wpcf7-submit:hover).
 */
const DEVIZY_SPINNER_FORMULARE = ".form-newsletter, .form-registrace, .form-vyhody-login, .form-kontakt";
const DEVIZY_NEWSLETTER_SPINNER_SVG =
  '<svg class="devizy-spinner animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>';

document.addEventListener(
  "submit",
  (event) => {
    const form = event.target;
    if (!form.closest(DEVIZY_SPINNER_FORMULARE)) {
      return;
    }
    const button = form.querySelector(".wpcf7-submit");
    if (!button || button.querySelector(".devizy-spinner")) {
      return;
    }
    button.insertAdjacentHTML("afterbegin", DEVIZY_NEWSLETTER_SPINNER_SVG);
  },
  true,
);

document.addEventListener("wpcf7submit", (event) => {
  const form = event.target;
  if (!form.closest(DEVIZY_SPINNER_FORMULARE)) {
    return;
  }
  form.querySelector(".wpcf7-submit .devizy-spinner")?.remove();
});

/*
 * GA4 měření konverzí — dataLayer push AŽ po úspěšné odpovědi serveru
 * (wpcf7mailsent = CF7 potvrdilo úspěšné odeslání), nikdy při kliku na
 * Odeslat a nikdy při chybě validace/serveru (ta by vyvolala jiný
 * event, wpcf7invalid/wpcf7mailfailed, na které nic nenavěšujeme).
 * ID formulářů viz inc/kontakt-formular.php (164),
 * inc/newsletter-formular.php (125), inc/registrace-formular.php (339).
 */
document.addEventListener("wpcf7mailsent", (event) => {
  const formId = event.detail?.contactFormId;
  window.dataLayer = window.dataLayer || [];

  if (164 === formId) {
    window.dataLayer.push({
      event: "generate_lead",
      form_type: "contact",
      placement: Alpine.store("modaly").kontaktPlacement || "subpage",
    });
    return;
  }

  if (125 === formId) {
    const wrapper = event.target.closest("[data-ga-placement]");
    window.dataLayer.push({
      event: "newsletter_signup",
      placement: wrapper ? wrapper.dataset.gaPlacement : "footer",
    });
    return;
  }

  if (339 === formId) {
    window.dataLayer.push({ event: "sign_up", method: "web_form" });
  }
});
